import React, {useContext, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import ProfileCard, { IProfileCardProps } from '../ProfileCard';
import withAppContext from "tbk-components/src/hooks/withAppContext";
import Loading from "tbk-components/src/components/Loading";

/**
 * @useTermData
 * @taxonomy team_member_category
 * @forceLinkedData
 */
export interface ITeamMemberCategoryTax {
	term_id?: number;
	name?: string;
}

export interface ITeamListingProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context,
	/**
	 * @noUI
	 */
	defaultTeamMembers?: IProfileCardProps[];
	/**
	 * Choose a category to filter by, or leave blank for all team members
	 */
	filterByCategories?: ITeamMemberCategoryTax[];
	/**
	 * Choose specific team members to display, or leave blank for all team members
	 */
	teamMembers?: IProfileCardProps[];
	/**
	 * Determine how many members per row (3 or 4)
	 */
	membersPerRow: '3' | '4';
}

/**
 * Calian Team Listing
 * @icon buddicons-buddypress-logo
 * @block
 */
const TeamListing: React.FC<ITeamListingProps> = withAppContext( ({
	context,
	id,
	className,
	classNames = [],
	defaultTeamMembers,
	filterByCategories,
	teamMembers,
	membersPerRow = 4,
}) => {
	//@ts-ignore
	const {Api} = useContext(context);
	const [displayTeamMembers, setDisplayTeamMembers] = useState<ITeamListingProps['teamMembers']>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [membersPerRowClass, setMembersPerRowClass] = useState<string>();

	const prevTeamMemberIDs = useRef<string>('');
	const prevCategoryIDs = useRef<string>('');

	useEffect(() => {
		if ( typeof( teamMembers ) === 'undefined' || teamMembers?.length === 0 ) {
			teamMembers = null;
		}

		if ( membersPerRow == 4 ) {
			setMembersPerRowClass(' col-xl-3');
		} else {
			setMembersPerRowClass('');
		}

		if ( defaultTeamMembers ) {
			setDisplayTeamMembers(defaultTeamMembers);
			return;
		} else {
			const teamMemberIDs = teamMembers?.map((member) => member.ID) ?? [];
			const categoryIDs = filterByCategories?.map((cat) => cat.term_id).sort() ?? [];

			const currentTeamKey = JSON.stringify(teamMemberIDs.sort());
			const currentCatKey = JSON.stringify(categoryIDs);

			if (
				prevTeamMemberIDs.current === currentTeamKey &&
				prevCategoryIDs.current === currentCatKey
			) {
				// No change, skip API call
				return;
			}

			// Store current keys for next run
			prevTeamMemberIDs.current = currentTeamKey;
			prevCategoryIDs.current = currentCatKey;

			setLoading(true);
			Api.populateTeamMembers({ 'teamMembers' : JSON.stringify( teamMemberIDs ), 'filterByCategories': filterByCategories })
				.then((results) => {
					setDisplayTeamMembers( results );
				}).finally(() => {
					setLoading(false);
				});
		}

	}, [ teamMembers, filterByCategories ]);

	if (loading) {
		return <Loading />;
	}

	return (
		<div id={id} className={className || cn('calian-team-listing', ...classNames)}>
			<div className="container">
				<div className="row">
					{displayTeamMembers?.map((member, index) => (
						<div
							key={index}
							className={`col-12 col-sm-6 col-md-6 col-lg-4${membersPerRowClass} text-center text-sm-start`}
						>
							<ProfileCard
								{...member}
								className="calian-team-listing__profile-card"
							/>
						</div>
					))}
				</div>
			</div>
		</div>
	);
});

export default TeamListing;
